import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { toast } from '@/components/ui/sonner';
import {
  websiteBannerControllerCreateBanner,
  websiteBannerControllerUpdateBannerById,
} from '@/generated/korza-api-client';
import type {
  CreateWebsiteBannerDto,
  UpdateWebsiteBannerDto,
  WebsiteBannerDto,
} from '@/generated/korza-api-client/types.gen';
import { tenantQueryKey, useTenant } from '@/hooks/use-tenant';
import { DEFAULT_BANNER } from '@/lib/website-banner-constants';

export interface UseWebsiteBannerSaveParams {
  bannerId: string | null;
  LL: {
    websiteBannerPage: {
      toasts: {
        saved: () => string;
        failedToSave: () => string;
      };
    };
  };
}

export function useWebsiteBannerSave({
  bannerId,
  LL,
}: UseWebsiteBannerSaveParams) {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoCreateAttemptedRef = useRef(false);

  const saveMutation = useMutation({
    mutationFn: async (
      payload: CreateWebsiteBannerDto
    ): Promise<WebsiteBannerDto> => {
      if (bannerId) {
        const body: UpdateWebsiteBannerDto = {
          content: payload.content,
          theme: payload.theme,
          customStyles: payload.customStyles,
          position: payload.position,
          extraHostnames: payload.extraHostnames,
          enabled: payload.enabled,
        };
        const res = await websiteBannerControllerUpdateBannerById({
          path: { bannerId },
          body,
        });
        if (res.data == null) throw new Error('Update failed');
        return res.data;
      }
      const createBody: CreateWebsiteBannerDto = {
        ...payload,
        tenantId: tenant.id,
      };
      const res = await websiteBannerControllerCreateBanner({
        body: createBody,
      });
      if (res.data == null) throw new Error('Create failed');
      return res.data;
    },
    onSuccess: () => {
      toast.success(LL.websiteBannerPage.toasts.saved());
      queryClient.invalidateQueries({ queryKey: tenantQueryKey(tenant.slug) });
      queryClient.invalidateQueries({ queryKey: ['website-banner'] });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? LL.websiteBannerPage.toasts.failedToSave());
    },
  });

  const shopifySetupComplete =
    searchParams.get('shopifySetupComplete') === 'true';

  // Clean up the search param only after bannerId is available, so the redirect
  // guard in the page doesn't fire during the gap between param removal and
  // tenant query resolution.
  useEffect(() => {
    if (bannerId && searchParams.has('shopifySetupComplete')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('shopifySetupComplete');
          return next;
        },
        { replace: true }
      );
    }
  }, [bannerId, searchParams, setSearchParams]);

  useEffect(() => {
    if (
      !bannerId &&
      (shopifySetupComplete || tenant.ecommercePlatform !== 'shopify') &&
      !saveMutation.isPending &&
      !autoCreateAttemptedRef.current
    ) {
      autoCreateAttemptedRef.current = true;
      saveMutation.mutate(DEFAULT_BANNER);
    }
  }, [bannerId, shopifySetupComplete, saveMutation, tenant.ecommercePlatform]);

  return {
    saveMutation,
    shopifySetupComplete,
  };
}
